// "Where things stand": a read-only summary of one issue, composed from its sections. Nothing
// here is written. What the reader clarified comes from what they want and why it matters;
// what is left comes from the open questions and the checking items; then the suggested next
// step, and the ways on.

import { Link, useLocation } from 'react-router-dom';

import type { ApiClient } from '../api/client.js';
import type { TopicView } from '../api/contract.js';
import { topicPhrase } from '../format.js';
import type { IssueState } from '../plan/useIssue.js';
import type { PlanState } from '../plan/usePlan.js';

export type IssueStandingPageProps = {
  state: PlanState;
  client: ApiClient;
  planId: string;
  topic: TopicView;
  issueId: string;
  issue: IssueState;
};

export function IssueStandingPage({
  state,
  planId,
  topic,
  issueId,
  issue,
}: IssueStandingPageProps): JSX.Element | null {
  const view = issue.view;
  // Set only by the navigation that follows "Save and finish for now"; a reload or a visit
  // from the issue page's link carries none of it.
  const saved = (useLocation().state as { saved?: boolean } | null)?.saved === true;
  if (state.snapshot === null) {
    return null;
  }
  if (issue.status === 'missing') {
    return (
      <section className="page">
        <p className="screen__problem">This isn’t available to you.</p>
      </section>
    );
  }
  if (view === null) {
    return null;
  }
  if (view.removed) {
    return (
      <section className="page issue">
        <h2 className="page__title">{view.title}</h2>
        <p className="note">This issue was removed from the list.</p>
      </section>
    );
  }

  const unsettled = view.checking.filter((row) => !row.settled);
  const nextStep = view.nextStep?.text ?? '';
  const arrangements = view.arrangements?.text ?? '';
  const topicPath = `/plans/${planId}/topics/${topic.key}`;
  return (
    <section className="page issue issueStanding">
      <div className="issue__head">
        <h2 className="page__title">{view.title}</h2>
        {saved ? (
          <span className="issue__links">
            <Link to={`${topicPath}/issues/${issueId}`}>Continue exploring →</Link>
          </span>
        ) : null}
      </div>
      {view.intention === null ? null : (
        <section className="issue__section">
          <h3 className="sectionLabel">What you want</h3>
          <p>{view.intention.text}</p>
          {view.intention.position === 'decided' ? null : (
            <p className="issue__note">
              {view.intention.position === null
                ? 'You haven’t said how settled you are.'
                : 'You haven’t settled on this yet.'}
            </p>
          )}
        </section>
      )}
      {view.rationale === null ? null : (
        <section className="issue__section">
          <h3 className="sectionLabel">Why it matters</h3>
          <p>{view.rationale.text}</p>
        </section>
      )}
      {arrangements === '' ? null : (
        <section className="issue__section">
          <h3 className="sectionLabel">Existing arrangements</h3>
          <p>{arrangements}</p>
        </section>
      )}
      {view.questions.length === 0 ? null : (
        <section className="issue__section">
          <h3 className="sectionLabel">What’s still open</h3>
          <ul className="issue__plain">
            {view.questions.map((row) => (
              <li key={row.id}>{row.question}</li>
            ))}
          </ul>
        </section>
      )}
      {unsettled.length === 0 ? null : (
        <section className="issue__section">
          <h3 className="sectionLabel">What needs checking</h3>
          <ul className="issue__plain">
            {unsettled.map((row) => (
              <li key={row.id}>
                {row.basis === 'expectation' ? 'Assumed: ' : ''}
                {row.text}
              </li>
            ))}
          </ul>
        </section>
      )}
      {nextStep === '' ? null : (
        <section className="issue__section">
          <h3 className="sectionLabel">Suggested next step</h3>
          <p>{nextStep}</p>
        </section>
      )}
      {saved ? (
        <div className="issueStanding__more">
          <Link className="issueStanding__link" to={topicPath}>
            Back to {topicPhrase(topic)} →
          </Link>
        </div>
      ) : null}
      <p className="issueStanding__privacy">
        Private to you. This isn't a legal document, and nothing has been shared with
        anyone.
      </p>
    </section>
  );
}
